/**
 * ExportDataSection — the data-portability affordance in Tallennetut tiedot
 * (033 US5, T041; FR-014..FR-016).
 *
 * A plain `<section>` action block, deliberately NOT a `CategoryCard`: it is an
 * action over the member's data, not a data category, so it carries no
 * app/legacy ownership line. Mirrors the erasure block's posture in
 * `routes/profile/MyDataSection.tsx`.
 *
 * Extracted into its own file rather than inlined: `MyDataSection.tsx` sits
 * under a hard 400-line architecture cap, and this flow carries five phases of
 * its own.
 *
 * READY SIGNAL, not an auto-download (see `useExport.ts` for the full
 * reasoning): the export resolves into a ready state offering BOTH FR-014
 * renderings, and the member picks. The download therefore always originates
 * from a real user gesture, and neither rendering is chosen on the member's
 * behalf.
 *
 * The status line is a pre-mounted `role="status"` polite live region — the
 * FeedbackModal posture: mount-and-fill in the same render is unreliable across
 * AT stacks, so the region is always present (sr-only and empty while idle)
 * and only its text changes.
 *
 * Token-backed classes only (no raw colours, no bracket classes).
 *
 * Owned by feature 033-data-ownership-and-lifecycle-foundations.
 * Contract: specs/033-.../contracts/my-data-api.md.
 */

import type { JSX } from 'react';

import { useTranslate } from '../i18n/useTranslate.js';
import { Button } from '../primitives/Button.js';
import { useExport } from './useExport.js';

const sectionClasses = 'flex flex-col gap-3 rounded-2xl border border-border bg-card p-4';
const headingClasses = 'text-sm font-semibold text-foreground';
const descriptionClasses = 'text-sm text-muted-foreground';
const statusClasses = 'text-sm text-foreground';
const actionRowClasses = 'flex flex-col gap-2 sm:flex-row';

export function ExportDataSection(): JSX.Element {
  const t = useTranslate();
  const { phase, start, download, reset } = useExport();

  // One status string per phase, or empty while idle. `ready` announces that
  // the artifacts are available; the download buttons render alongside.
  const statusMessage = ((): string => {
    switch (phase.kind) {
      case 'idle':
        return '';
      case 'pending':
        return t('myData.export.pending');
      case 'ready':
        return t('myData.export.ready');
      case 'error':
        return t('myData.export.error');
      default: {
        // Exhaustiveness: a new phase must be handled here, not fall through
        // to a blank status line.
        const unreachable: never = phase;
        return unreachable;
      }
    }
  })();

  return (
    <section aria-label={t('myData.export.section.heading')} className={sectionClasses}>
      <h4 className={headingClasses}>{t('myData.export.section.heading')}</h4>
      <p className={descriptionClasses}>{t('myData.export.section.description')}</p>

      {/* Pre-mounted live region: AT registers it before its content changes. */}
      <p role="status" className={phase.kind === 'idle' ? 'sr-only' : statusClasses}>
        {statusMessage}
      </p>

      {(phase.kind === 'idle' || phase.kind === 'pending' || phase.kind === 'error') && (
        <div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={phase.kind === 'pending'}
            onClick={() => {
              void start();
            }}
          >
            {phase.kind === 'pending' ? t('myData.export.pending') : t('myData.export.trigger')}
          </Button>
        </div>
      )}

      {phase.kind === 'ready' && (
        <div className={actionRowClasses}>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => download(phase.result.machineReadable)}
          >
            {t('myData.export.download.json')}
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => download(phase.result.humanReadable)}
          >
            {t('myData.export.download.text')}
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={reset}>
            {t('myData.export.close')}
          </Button>
        </div>
      )}
    </section>
  );
}
